import React, { useState, useEffect } from 'react';
import { Radio, Input, Button, RadioChangeEvent } from 'antd';
import { formatCurrency } from 'utils';

import { ProductData } from 'types';
// import { LabelWithRequired } from 'components/common';
type QuantityMatchType = 'match' | 'different';

interface ProductDetailFormProps {
  product?: ProductData;
  onSave?: (productId: string, actualQuantity: number | null, reason?: string) => void;
  className?: string;
  hasDescription?: boolean;
}

const ProductDetailForm: React.FC<ProductDetailFormProps> = ({
  product,
  onSave,
  className,
  hasDescription = false,
}) => {
  const [quantityType, setQuantityType] = useState<QuantityMatchType>('match');
  const [actualQuantity, setActualQuantity] = useState<number | undefined>(product?.actualQuantity);
  const [reason, setReason] = useState<string | undefined>(product?.reason || '');
  // Calculate values based on current inputs
  const difference = actualQuantity !== undefined && product ? actualQuantity - product.quantity : 0;
  const subtotal = product ? product.quantity * product.unitPrice : 0;
  const total = actualQuantity !== undefined && product ? actualQuantity * product.unitPrice : subtotal;
  // Format difference with sign
  const formattedDifference = difference > 0 ? `+${difference}` : difference.toString();
  // const [compensateOption, setCompensateOption] = useState<'compensate' | 'refund'>('compensate');
  // Style for difference text
  const differenceStyle =
    difference < 0
      ? { color: 'var(--sematicErrorColor500)' }
      : difference > 0
        ? { color: 'var(--sematicWarningColor)' }
        : {};

  const handleQuantityTypeChange = (e: RadioChangeEvent) => {
    const value = e.target.value as QuantityMatchType;
    setQuantityType(value);

    if (value === 'match' && product && (product.actualQuantity === undefined || product.actualQuantity === null)) {
      setActualQuantity(product.quantity);
    }
  };

  const handleActualQuantityChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setActualQuantity(numValue);
    } else if (value === '') {
      setActualQuantity(undefined);
    }
  };

  const handleSave = () => {
    if (product) {
      if (quantityType === 'match') {
        onSave?.(product.id, product.quantity, reason);
      } else {
        onSave?.(product.id, actualQuantity !== undefined ? actualQuantity : null, reason);
      }
    }
  };

  useEffect(() => {
    if (product) {
      if (product.actualQuantity !== undefined && product.actualQuantity !== null) {
        setActualQuantity(product.actualQuantity);
        setQuantityType(product.actualQuantity !== product.quantity ? 'different' : 'match');
      } else {
        setActualQuantity(product.quantity);
        setQuantityType('match');
      }
    } else {
      setQuantityType('match');
      setActualQuantity(undefined);
    }
  }, [product]);
  const handleReset = () => {
    // Reset to default values without removing selection
    setQuantityType('match');
    setActualQuantity(product?.quantity);
    if (product && onSave) {
      onSave(product.id, null);
    }
  };
  // Render empty state
  if (!product) {
    return (
      // <StickySidebarLayout top={280}>
      <div
        className={`h-full  2xl:w-[444px] xl:w-[360px] lg:w-[296px]  bg-white rounded-2xl flex flex-col ${className}`}
      >
        <header className='px-6 py-3'>
          <h2 className='text-[var(--primaryColor)] text-xl font-bold'>THÔNG TIN SẢN PHẨM</h2>
        </header>
        <div className='flex-1 flex items-center justify-center'>
          <p className='text-red-500 text-sm'>Chọn 1 sản phẩm để cập nhật số lượng thực tế</p>
        </div>
      </div>
      // </StickySidebarLayout>
    );
  }

  return (
    // <StickySidebarLayout top={280}>
    <div className={`h-full  2xl:w-[444px] xl:w-[360px] lg:w-[296px]  bg-white rounded-2xl flex flex-col ${className}`}>
      {/* Header */}
      <header className='px-6 py-3'>
        <h2 className='text-[var(--primaryColor)] text-xl font-bold'>THÔNG TIN SẢN PHẨM</h2>
      </header>

      <div className='flex-1 px-6 py-3 flex flex-col overflow-y-auto'>
        {/* Product info section */}
        <div className='flex gap-3 mb-6'>
          <div className='w-[84px] h-[84px] bg-[var(--textBlack12)] rounded-lg shrink-0'>
            {product.imageUrl && (
              <img src={product.imageUrl} alt={product.name} className='w-full h-full object-cover rounded-lg' />
            )}
          </div>

          <div>
            <h3 className='text-black text-xl font-bold mb-1'>{product.name}</h3>
            <div className='flex items-center gap-2 text-sm'>
              <span className='w-20 text-[var(textBlack56)] font-medium'>Đơn giá:</span>
              <span className='text-[var(--primaryColor500)] font-bold'>
                {formatCurrency(product.unitPrice)} {product.currencyUnit}
              </span>
            </div>
            <div className='flex items-center gap-2 text-sm'>
              <span className='w-20 text-[var(textBlack56)] font-medium'>Danh mục:</span>
              <span className='text-[var(--primaryColor)] font-bold'>{product.category}</span>
            </div>
          </div>
        </div>

        {/* Quantity and pricing section */}
        <div className='space-y-3'>
          {/* Expected quantity */}
          <div className='flex items-center'>
            <span className='w-[120px] text-[var(textBlack56)] text-sm'>Số lượng</span>
            <span className='text-[var(--primaryColor)] text-sm font-medium'>{product.quantity}</span>
            <span className='text-[var(textBlack56)] text-sm font-medium ml-2'>{product.unit}</span>
          </div>
          {/* Subtotal */}
          <div className='flex items-center'>
            <span className='w-[120px] text-[var(--primaryColor)] font-bold'>Tạm tính</span>
            <span
              className='text-[var(--primaryColor)] font-bold'
              style={difference !== 0 ? { color: 'var(--sematicErrorColor500) ', textDecoration: 'line-through' } : {}}
            >
              {formatCurrency(subtotal)}
            </span>
            <span className='text-[var(textBlack56)] font-bold ml-2'>{product.currencyUnit}</span>
          </div>
          <hr className='my-2' />

          {/* Radio options */}
          <Radio.Group value={quantityType} onChange={handleQuantityTypeChange} className='flex flex-col gap-2'>
            <Radio value='match' className='custom-radio'>
              Số lượng thực tế trùng khớp
            </Radio>
            <Radio value='different' className='custom-radio'>
              Số lượng thực tế khác
            </Radio>
          </Radio.Group>

          {/* Input field */}
          <Input
            placeholder='Nhập vào số lượng thực tế...'
            value={quantityType === 'different' ? actualQuantity : undefined}
            onChange={(e) => handleActualQuantityChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e') {
                e.preventDefault();
              }
            }}
            disabled={quantityType === 'match'}
            min={0}
            type='number'
            className='w-full'
          />

          {/* Difference */}
          <div className='flex items-center'>
            <span className='w-[120px] text-[var(textBlack56)] text-sm'>Chênh lệch</span>
            <span className='text-sm font-medium' style={differenceStyle}>
              {formattedDifference}
            </span>
            <span className='text-[var(textBlack56)] text-sm font-medium ml-2'>{product.unit}</span>
          </div>

          {/* Warning message */}
          {difference !== 0 && (
            <>
              <p className='text-xs' style={differenceStyle}>
                Số lượng đang chênh lệch lớn, chúng tôi đã tính toán lại giá tiền của sản phẩm
              </p>
              {/* {difference < 0 && (
                  <Radio.Group
                    className='w-full flex flex-col gap-3 custom-radio custom-group-radio radio-border-primary mt-4'
                    value={compensateOption}
                    onChange={(e) => setCompensateOption(e.target.value)}
                  >
                    <Radio value='compensate' className='w-full !border-[var(--textBlack12)] border rounded-xl p-4'>
                      <div>
                        <div className='font-medium title-bold'>Bù hàng còn thiếu</div>
                        <div className='text-sm text-[var(--textBlack56)]'>Số lượng hàng thiếu sẽ được bù</div>
                      </div>
                    </Radio>
                    <Radio value='refund' className='w-full !border-[var(--textBlack12)] border rounded-xl p-4'>
                      <div>
                        <div className='font-medium title-bold'>Hoàn tiền, không bù hàng</div>
                        <div className='text-sm text-[var(--textBlack56)]'>
                          Số tiền tương ứng sản phẩm còn thiếu sẽ được hoàn trả
                        </div>
                      </div>
                    </Radio>
                  </Radio.Group>
                )} */}
            </>
          )}

          {/* Total */}
          <div className='flex items-center'>
            <span className='w-[120px] text-[var(--primaryColor)] font-bold'>Tổng số tiền</span>
            <span className='text-[var(--primaryColor500)] font-bold'>{formatCurrency(total)}</span>
            <span className='text[var(--primaryColor300)] font-bold ml-2'>{product.currencyUnit}</span>
          </div>
          {hasDescription && (
            <div className='mt-2'>
              <span className='text-[var(--primaryColor)] text-sm font-medium'>Lý do</span>
              <Input.TextArea
                value={reason}
                className='mt-1 rounded-md p-2'
                rows={3}
                onChange={(e) => setReason(e.target.value)}
                placeholder='Nhập lý do...'
              />
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className='px-6 py-3 flex justify-center gap-2'>
        <Button onClick={handleReset}>Xóa dữ liệu</Button>
        <Button type='primary' onClick={handleSave}>
          Lưu thông tin
        </Button>
      </div>
    </div>
    // </StickySidebarLayout>
  );
};

export default ProductDetailForm;
